/**
 * MOD-INFANT — Frais de modification / annulation d'un bébé.
 *
 * Un bébé (INF, moins de 2 ans) n'occupe pas de siège : les frais affichés pour lui valent
 * 10 % du frais adulte issu de la règle de frais matchée, arrondis à l'euro le plus proche.
 *
 * Règle d'**affichage** (devis, contrat de réservation, conditions web) : elle ne modifie
 * pas le montant porté par la règle de frais (`cancellation-fee-rule.service.ts`), donc ni
 * l'échéancier ni les montants réellement facturés / remboursés.
 */
export const INFANT_FEE_RATE = 0.1;
/**
 * Mention à accoler à une ligne de conditions quand le dossier compte au moins un bébé.
 *
 * 250 € → `'25 € pour un bébé (- 2 ans)'` · 0 € → `undefined` (rien à annoncer)
 */
export function buildInfantFeeNote(adultFeeInEuros) {
    const infantFee = computeInfantFee(adultFeeInEuros);
    if (infantFee <= 0)
        return undefined;
    return `${infantFee} € pour un bébé (- 2 ans)`;
}
/**
 * Frais bébé dérivé du frais adulte (tous deux en euros), arrondi à l'euro le plus proche.
 *
 * Exemples : 250 € → 25 € · 290 € → 29 € · 195 € → 20 € · 0 € → 0 €
 */
export function computeInfantFee(adultFeeInEuros) {
    if (!Number.isFinite(adultFeeInEuros) || adultFeeInEuros <= 0)
        return 0;
    return Math.round(adultFeeInEuros * INFANT_FEE_RATE);
}
/**
 * Retrouve le montant d'un `feeDescription` produit par `buildFeeDescription`, quand seule
 * la description texte est disponible (conditions stockées sur la réservation, conditions web).
 *
 * `'avec 250.00 € de frais'` → `250` · `'sans frais'` → `null`
 */
export function parseFeeAmountFromDescription(feeDescription) {
    if (!feeDescription)
        return null;
    const match = feeDescription.match(/avec\s+([\d\s.,]+)\s*€\s+de frais/);
    if (!match)
        return null;
    const amount = Number.parseFloat(match[1].replaceAll(/\s/g, '').replace(',', '.'));
    return Number.isFinite(amount) ? amount : null;
}
